// Aperçu par révision (lot G2 v0.5.0 — B-201…B-207, ART-0169) : client de `/api/v1/previews`.
//
// `GET /previews/authorize` n'a PAS de fonction ici : il est appelé par l'`auth_request` du
// proxy d'aperçu, jamais par l'interface. L'interface obtient un jeton court (`access`) et
// ouvre l'aperçu sur SON origine (port dédié), où le proxy l'échange contre un cookie.
import { request, ApiError, httpMessage, BASE } from './http'
import { getToken } from '../auth/session'
import type {
  PreviewAccess, PreviewAnnotationInput, PreviewAnnotationListResponse, PreviewAnnotationResult,
  PreviewComparison, PreviewDetail, PreviewEnvironment, PreviewListResponse, PreviewRestartOutcome, PreviewRestartResult,
  PreviewRevisionListResponse, PreviewShare, PreviewShareCreateInput, PreviewShareListResponse, PreviewShareOpened,
  PreviewStartInput, PreviewStartResult, PreviewSurvey,
} from './types/previews'

function query(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [cle, valeur] of Object.entries(params)) {
    if (valeur !== undefined && valeur !== null && valeur !== '') q.set(cle, String(valeur))
  }
  return q.toString()
}

const seg = encodeURIComponent

export const previewsApi = {
  /** Aperçus de l'espace (vivants et récents), plafond de l'espace et pool de ports. */
  listPreviews: (workspaceId: string, appSlug?: string) =>
    request<PreviewListResponse>(`/previews?${query({ workspace_id: workspaceId, app_slug: appSlug })}`),
  /** Démarre l'aperçu d'une révision (met en file `start_preview`) ; 200 si déjà vivant. */
  startPreview: (input: PreviewStartInput) =>
    request<PreviewStartResult>('/previews', { method: 'POST', body: JSON.stringify(input) }),
  /** Détail d'un aperçu, avec l'identité de sa révision (contrôles, carte, modèle). */
  getPreview: (workspaceId: string, id: string) =>
    request<PreviewDetail>(`/previews/${seg(id)}?${query({ workspace_id: workspaceId })}`),
  /** Demande l'arrêt (met en file `stop_preview`). */
  stopPreview: (workspaceId: string, id: string) =>
    request<PreviewEnvironment>(`/previews/${seg(id)}/stop`, {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }),
    }),
  /** MJ-16 : redémarre l'aperçu (met en file `restart_preview`) : conteneurs recréés, données gardées. */
  restartPreview: (workspaceId: string, id: string) =>
    request<PreviewRestartResult>(`/previews/${seg(id)}/restart`, {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }),
    }),
  /** MJ-16 : issue d'un redémarrage — état, cause, recréation prouvée, volume de données, notes. */
  getPreviewRestart: (workspaceId: string, id: string, jobId: string) =>
    request<PreviewRestartOutcome>(`/previews/${seg(id)}/restarts/${seg(jobId)}?${query({ workspace_id: workspaceId })}`),
  /** Jeton court d'accès à l'origine de l'aperçu (lecture : un lecteur peut l'obtenir). */
  getPreviewAccess: (workspaceId: string, id: string) =>
    request<PreviewAccess>(`/previews/${seg(id)}/access?${query({ workspace_id: workspaceId })}`),
  /** Annotation épinglée → anomalie `source=preview_annotation`. */
  createPreviewAnnotation: (id: string, input: PreviewAnnotationInput) =>
    request<PreviewAnnotationResult>(`/previews/${seg(id)}/annotations`, {
      method: 'POST', body: JSON.stringify(input),
    }),
  listPreviewAnnotations: (workspaceId: string, id: string) =>
    request<PreviewAnnotationListResponse>(`/previews/${seg(id)}/annotations?${query({ workspace_id: workspaceId })}`),
  /** Révisions (jobs `scaffold_app`) de l'espace, la plus récente d'abord. */
  listPreviewRevisions: (workspaceId: string, appSlug?: string) =>
    request<PreviewRevisionListResponse>(`/previews/revisions?${query({ workspace_id: workspaceId, app_slug: appSlug })}`),
  /** Relevé d'une révision : écrans, routes, balises interactives, captures disponibles. */
  getPreviewSurvey: (workspaceId: string, revision: string) =>
    request<PreviewSurvey>(`/previews/revisions/${seg(revision)}/survey?${query({ workspace_id: workspaceId })}`),
  /** Capture PNG d'un écran à une largeur (binaire authentifié : jamais une `<img src>` nue). */
  fetchPreviewCapture: async (workspaceId: string, revision: string, screenId: string, width: number): Promise<Blob> => {
    const token = getToken()
    const response = await fetch(
      `${BASE}/previews/revisions/${seg(revision)}/captures/${seg(screenId)}/${width}?${query({ workspace_id: workspaceId })}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )
    if (!response.ok) {
      throw new ApiError(response.status, httpMessage(response.status),
        await response.text().catch(() => undefined))
    }
    return response.blob()
  },
  /** Lot A 0.5.x (B-205) : crée un lien daté (24 h par défaut) sur un aperçu vivant. */
  createPreviewShare: (id: string, input: PreviewShareCreateInput) =>
    request<PreviewShare>(`/previews/${seg(id)}/shares`, { method: 'POST', body: JSON.stringify(input) }),
  /** Liens de partage ACTIFS d'un aperçu (ni révoqués ni échus) et bornes de durée. */
  listPreviewShares: (workspaceId: string, id: string) =>
    request<PreviewShareListResponse>(`/previews/${seg(id)}/shares?${query({ workspace_id: workspaceId })}`),
  /** Révoque un lien (son auteur ou le propriétaire du projet) ; idempotent. */
  revokePreviewShare: (workspaceId: string, id: string, shareId: string) =>
    request<PreviewShare>(`/previews/${seg(id)}/shares/${seg(shareId)}/revoke`, {
      method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }),
    }),
  /** Ouvre un lien : 404 inconnu ou autre organisation, 403 non-membre, 410 révoqué ou échu. */
  openPreviewShare: (shareId: string) =>
    request<PreviewShareOpened>(`/previews/shares/${seg(shareId)}`),
  /** Comparaison de deux révisions : décomptes, écrans appariés, captures changées. */
  comparePreviewRevisions: (workspaceId: string, base: string, head: string) =>
    request<PreviewComparison>(`/previews/compare?${query({ workspace_id: workspaceId, base, head })}`),
}
